import { Suspense, lazy } from 'react'
import { Routes, Route, Outlet, Link, NavLink, useNavigate, useLocation } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/components/ui/Toast'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { RequireRole } from '@/routes/RequireRole'
import { Logo } from '@/components/ui/Logo'
import Home from '@/pages/public/Home'
import NotFound from '@/pages/public/NotFound'

// ── Lazy auth pages ───────────────────────────────────────────────────────────
const LoginPage        = lazy(() => import('@/pages/public/LoginPage'))
const RegisterPage     = lazy(() => import('@/pages/public/RegisterPage'))
const ForgotPassword   = lazy(() => import('@/pages/public/ForgotPasswordPage'))
const ResetPassword    = lazy(() => import('@/pages/public/ResetPasswordPage'))
const AuthConfirm      = lazy(() => import('@/pages/public/AuthConfirm'))

// ── Lazy public catalog pages ─────────────────────────────────────────────────
const SearchResults         = lazy(() => import('@/pages/public/SearchResults'))
const PropertyDetail        = lazy(() => import('@/pages/public/PropertyDetail'))
const ListYourPropertyPage  = lazy(() => import('@/pages/public/ListYourPropertyPage'))
const CreditsPage           = lazy(() => import('@/pages/public/CreditsPage'))
const HelpCenter            = lazy(() => import('@/pages/public/HelpCenter'))
const HelpArticle           = lazy(() => import('@/pages/public/HelpArticle'))
const LegalPage             = lazy(() => import('@/pages/public/LegalPage'))

// ── Lazy account pages ────────────────────────────────────────────────────────
const ProfilePage        = lazy(() => import('@/pages/account/ProfilePage'))
const BookReview         = lazy(() => import('@/pages/account/BookReview'))
const BookingConfirmed   = lazy(() => import('@/pages/account/BookingConfirmed'))
const BookingFailed      = lazy(() => import('@/pages/account/BookingFailed'))
const BookingsPage       = lazy(() => import('@/pages/account/BookingsPage'))
const BookingDetailPage  = lazy(() => import('@/pages/account/BookingDetailPage'))

// ── Lazy extranet ─────────────────────────────────────────────────────────────
const ExtranetRoutes = lazy(() => import('@/routes/ExtranetRoutes'))

// ── Lazy admin ────────────────────────────────────────────────────────────────
const AdminRoutes = lazy(() => import('@/routes/AdminRoutes'))

// ── Shared ────────────────────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner size="lg" />
    </div>
  )
}

// ── User menu (authenticated nav) ─────────────────────────────────────────────

function UserMenu() {
  const { profile, signOut } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()

  async function handleSignOut() {
    await signOut()
    toast({ title: 'Signed out', variant: 'success' })
    navigate('/')
  }

  const itemClass =
    'flex w-full cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-gray-700 outline-none hover:bg-gray-100 focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50'

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          aria-label="Open user menu"
          className="flex items-center gap-2 rounded-full p-1 text-kasa-green-100 hover:bg-kasa-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <Avatar
            src={profile?.avatar_url}
            name={profile?.full_name}
            size="sm"
          />
          <span className="hidden text-sm font-medium sm:block">
            {profile?.full_name?.split(' ')[0] ?? 'Account'}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 opacity-70"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-50 min-w-[180px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg"
        >
          {/* Role badge */}
          <div className="px-3 py-2 text-xs text-gray-400 capitalize">
            {profile?.role}
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-gray-100" />

          <DropdownMenu.Item asChild>
            <Link to="/account/bookings" className={itemClass}>
              My bookings
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Item asChild>
            <Link to="/account/profile" className={itemClass}>
              Profile
            </Link>
          </DropdownMenu.Item>

          {(profile?.role === 'owner' || profile?.role === 'admin') && (
            <DropdownMenu.Item asChild>
              <Link to="/extranet" className={itemClass}>
                Extranet
              </Link>
            </DropdownMenu.Item>
          )}

          {profile?.role === 'admin' && (
            <DropdownMenu.Item asChild>
              <Link to="/admin" className={itemClass}>
                Admin
              </Link>
            </DropdownMenu.Item>
          )}

          <DropdownMenu.Separator className="my-1 h-px bg-gray-100" />

          <DropdownMenu.Item
            onSelect={handleSignOut}
            className={itemClass + ' text-red-600 hover:bg-red-50 focus:bg-red-50'}
          >
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

// ── Root layout ───────────────────────────────────────────────────────────────

function RootLayout() {
  const { session, loading } = useAuth()
  const { pathname } = useLocation()

  // Extranet & admin are full-height app shells with their own chrome — the
  // marketing footer doesn't belong there (it makes the page scroll past the
  // dashboard's own sidebar). Keep the top header for navigation/auth.
  const isDashboard = pathname.startsWith('/extranet') || pathname.startsWith('/admin')

  return (
    // Dashboards are a fixed-viewport shell so only the inner panel scrolls
    // (no second window scrollbar); public pages scroll normally.
    <div className={isDashboard ? 'flex h-dvh flex-col overflow-hidden' : 'flex min-h-dvh flex-col'}>
      <header className="sticky top-0 z-40 border-b border-kasa-green-700 bg-kasa-green-600">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          {/* Logo — inline SVG with currentColor so text-white makes it visible on green */}
          <Link to="/" className="flex-shrink-0" aria-label="KasaGO — go to homepage">
            <Logo className="h-8 w-auto" />
          </Link>

          {/* Centre nav — Iterations 3+ */}
          <nav className="hidden flex-1 items-center gap-1 text-sm font-medium text-kasa-green-100 md:flex">
            <NavLink
              to="/search"
              className={({ isActive }) =>
                isActive
                  ? 'rounded-md bg-kasa-green-700 px-3 py-2 text-white'
                  : 'rounded-md px-3 py-2 hover:bg-kasa-green-700 hover:text-white'
              }
            >
              Find a stay
            </NavLink>
          </nav>

          {/* Right side — list property + auth */}
          <div className="flex items-center gap-3">
            <Link
              to="/list-your-property"
              className="hidden text-sm font-medium text-kasa-green-100 hover:text-white sm:block"
            >
              List your property
            </Link>
            {loading ? (
              <Spinner size="sm" className="border-kasa-green-300 border-t-white" />
            ) : session ? (
              <UserMenu />
            ) : (
              <>
                <Button variant="ghost" asChild
                  className="text-kasa-green-100 hover:bg-kasa-green-700 hover:text-white"
                >
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button variant="cta" asChild size="sm">
                  <Link to="/register">Register</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className={isDashboard ? 'flex-1 min-h-0 overflow-y-auto' : 'flex-1'}>
        <Outlet />
      </main>

      {!isDashboard && (
      <footer className="border-t border-gray-200 bg-gray-50 py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3">
            <nav aria-label="KasaGO">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">KasaGO</h3>
              <ul className="mt-3 space-y-2 text-sm text-gray-600">
                <li><Link to="/search"             className="hover:text-kasa-green-600">Find a stay</Link></li>
                <li><Link to="/list-your-property" className="hover:text-kasa-green-600">List your property</Link></li>
                <li><Link to="/help"               className="hover:text-kasa-green-600">Help Center</Link></li>
                <li><Link to="/credits"            className="hover:text-kasa-green-600">Image credits</Link></li>
              </ul>
            </nav>
            <nav aria-label="Terms and settings">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Terms &amp; settings</h3>
              <ul className="mt-3 space-y-2 text-sm text-gray-600">
                <li><Link to="/legal/privacy"         className="hover:text-kasa-green-600">Privacy Notice</Link></li>
                <li><Link to="/legal/terms"           className="hover:text-kasa-green-600">Terms of Service</Link></li>
                <li><Link to="/legal/accessibility"   className="hover:text-kasa-green-600">Accessibility Statement</Link></li>
                <li><Link to="/legal/partner-dispute" className="hover:text-kasa-green-600">Partner dispute</Link></li>
                <li><Link to="/legal/modern-slavery"  className="hover:text-kasa-green-600">Modern Slavery Statement</Link></li>
                <li><Link to="/legal/human-rights"    className="hover:text-kasa-green-600">Human Rights Statement</Link></li>
              </ul>
            </nav>
            <nav aria-label="Contact">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Contact</h3>
              <ul className="mt-3 space-y-2 text-sm text-gray-600">
                <li><a href="mailto:support@kasagoph.com" className="hover:text-kasa-green-600">support@kasagoph.com</a></li>
                <li><a href="https://kasagoph.com" className="hover:text-kasa-green-600">kasagoph.com</a></li>
              </ul>
            </nav>
          </div>
          <p className="mt-8 border-t border-gray-200 pt-6 text-xs text-gray-400">
            © {new Date().getFullYear()} KasaGO. All rights reserved.
          </p>
        </div>
      </footer>
      )}
    </div>
  )
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<RootLayout />}>
          {/* ── Public ──────────────────────────────────────────────────── */}
          <Route index element={<Home />} />
          <Route path="login"           element={<LoginPage />} />
          <Route path="register"        element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password"  element={<ResetPassword />} />
          <Route path="auth/confirm"    element={<AuthConfirm />} />
          <Route path="search"               element={<SearchResults />} />
          <Route path="properties/:slug"   element={<PropertyDetail />} />
          <Route path="list-your-property" element={<ListYourPropertyPage />} />
          <Route path="credits"           element={<CreditsPage />} />
          <Route path="help"              element={<HelpCenter />} />
          <Route path="help/:slug"        element={<HelpArticle />} />
          <Route path="legal/:slug"       element={<LegalPage />} />
          {/* ── Booking flow (Iteration 4) ──────────────────────────────── */}
          <Route
            path="book"
            element={
              <RequireRole roles={['guest', 'owner', 'admin']}>
                <BookReview />
              </RequireRole>
            }
          />
          <Route
            path="booking/confirmed"
            element={
              <RequireRole roles={['guest', 'owner', 'admin']}>
                <BookingConfirmed />
              </RequireRole>
            }
          />
          <Route path="booking/failed" element={<BookingFailed />} />

          {/* ── Account (any authenticated role) ────────────────────────── */}
          <Route
            path="account/profile"
            element={
              <RequireRole roles={['guest', 'owner', 'admin']}>
                <ProfilePage />
              </RequireRole>
            }
          />
          {/* ── Account bookings (Iteration 5) ──────────────────────── */}
          <Route
            path="account/bookings"
            element={
              <RequireRole roles={['guest', 'owner', 'admin']}>
                <BookingsPage />
              </RequireRole>
            }
          />
          <Route
            path="account/bookings/:id"
            element={
              <RequireRole roles={['guest', 'owner', 'admin']}>
                <BookingDetailPage />
              </RequireRole>
            }
          />

          {/* ── Extranet — any authenticated user may enter.
               Guests land in ExtranetLayout which redirects to /extranet/onboarding
               where the wizard calls become_owner() before any property data is shown.
               The DB-level owns_property() check is the real access boundary.     ── */}
          <Route
            path="extranet/*"
            element={
              <RequireRole roles={['guest', 'owner', 'admin']}>
                <ExtranetRoutes />
              </RequireRole>
            }
          />

          {/* ── Admin (admin) — Iteration 7 ─────────────────────────────── */}
          <Route
            path="admin/*"
            element={
              <RequireRole roles={['admin']}>
                <AdminRoutes />
              </RequireRole>
            }
          />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  )
}
